/**
 * The mic in the composer, and the clip it produces (owner decision
 * 2026-08-22).
 *
 * Three states in one button, because they are three states of one thing:
 * idle, recording, and holding a clip that has been recorded and not yet sent.
 * The third is the one this product insists on — a voice note is played back
 * before it goes, so nobody sends a recording of themselves clearing their
 * throat to somebody who is judging them.
 *
 * A browser that cannot record renders NOTHING. Not a disabled button with a
 * sad tooltip: an affordance that is never going to work is furniture, and the
 * composer is better without it.
 */
import { AudioBubble, formatDuration } from "./audio-bubble";
import {
  canRecord,
  DISCARD_LABEL,
  MIC_LABEL,
  RECORDING_COPY,
  STOP_LABEL,
  type VoiceRecorder,
} from "./voice";

export const SEND_VOICE_LABEL = "Send the voice note";
export const PREVIEW_LABEL = "Your recording, before you send it";

const FOCUS =
  "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-focus/70 " +
  "focus-visible:ring-offset-2 focus-visible:ring-offset-bg";

function MicGlyph() {
  return (
    <svg viewBox="0 0 20 20" className="h-5 w-5" fill="none" aria-hidden="true">
      <path
        d="M10 3.25a2 2 0 0 1 2 2v4.5a2 2 0 1 1-4 0v-4.5a2 2 0 0 1 2-2ZM5.5 9.25a4.5 4.5 0 0 0 9 0M10 14v3"
        stroke="currentColor"
        strokeWidth="1.4"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

function StopGlyph() {
  return (
    <svg viewBox="0 0 20 20" className="h-4 w-4" aria-hidden="true">
      <rect x="5.5" y="5.5" width="9" height="9" rx="2" fill="currentColor" />
    </svg>
  );
}

function CrossGlyph() {
  return (
    <svg viewBox="0 0 20 20" className="h-3.5 w-3.5" fill="none" aria-hidden="true">
      <path d="M6 6l8 8M14 6l-8 8" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
    </svg>
  );
}

export type VoiceClip = { blob: Blob; filename: string; durationMs: number };

/**
 * The button, beside the field. The PREVIEW is `VoicePreview`, above it —
 * separate components over one `useVoiceRecorder`, held by whoever renders the
 * composer, because a player wants the width of the composer and this is a
 * 36-pixel circle at the end of a row.
 */
export function VoiceButton({
  voice,
  disabled = false,
}: {
  voice: VoiceRecorder;
  disabled?: boolean;
}) {
  if (!canRecord()) return null;

  return (
    <button
      type="button"
      aria-label={voice.recording ? STOP_LABEL : MIC_LABEL}
      aria-pressed={voice.recording}
      data-recording={voice.recording ? "true" : "false"}
      disabled={disabled}
      onClick={() => (voice.recording ? voice.stop() : voice.start())}
      className={`flex h-11 w-9 shrink-0 items-center justify-center rounded-full
                  transition-colors duration-75 disabled:pointer-events-none disabled:opacity-50
                  ${voice.recording ? "text-danger" : "text-faint hover:text-text-soft"} ${FOCUS}`}
    >
      {voice.recording ? <StopGlyph /> : <MicGlyph />}
    </button>
  );
}

/**
 * Everything that has to appear ABOVE the field: the recording counter, the
 * clip waiting to be sent, and the one sentence a failure gets.
 *
 * It is a separate export because `TurnInput` renders it in its `chip` slot,
 * where the pending-attachment chip goes — the two are the same idea (a thing
 * in flight, above the field, pushing nothing about) and they are mutually
 * exclusive in practice.
 */
export function VoicePreview({
  voice,
  onSend,
}: {
  voice: VoiceRecorder;
  onSend: (clip: VoiceClip) => void;
}) {
  if (voice.error) {
    return (
      <p className="mb-2 text-xs text-faint" role="status">
        {voice.error}
      </p>
    );
  }

  if (voice.recording) {
    return (
      <p className="nob-rec mb-2" role="status" data-testid="recording-line">
        <span className="inline-block h-2 w-2 rounded-full bg-current" aria-hidden="true" />
        {RECORDING_COPY} · {formatDuration(voice.elapsedMs)}
      </p>
    );
  }

  const clip = voice.clip;
  if (!clip) return null;

  return (
    <div
      className="mb-2 flex items-center gap-2 rounded-xl border border-border bg-surface/70 px-3 py-2"
      data-testid="voice-preview"
      role="group"
      aria-label={PREVIEW_LABEL}
    >
      <span className="min-w-0 flex-1 text-text-soft">
        <AudioBubble src={clip.url} durationMs={clip.durationMs} label={PREVIEW_LABEL} />
      </span>
      <button
        type="button"
        aria-label={DISCARD_LABEL}
        onClick={voice.discard}
        className={`flex h-7 w-7 shrink-0 items-center justify-center rounded-full text-faint
                    transition-colors duration-75 hover:text-text-soft ${FOCUS}`}
      >
        <CrossGlyph />
      </button>
      <button
        type="button"
        onClick={() => {
          onSend({ blob: clip.blob, filename: clip.filename, durationMs: clip.durationMs });
          voice.discard();
        }}
        className={`shrink-0 rounded-full bg-accent px-3 py-1.5 text-xs font-semibold text-accent-fg
                    transition-colors duration-75 hover:bg-accent-soft ${FOCUS}`}
      >
        {SEND_VOICE_LABEL}
      </button>
    </div>
  );
}
